import { AppRoutesService, CumLaudeRoute, CumLaudeRouteData, RouteDef } from '@cumlaude/shared-components-menu';
import { IconName } from '@cumlaude/shared-components-icons';
import { computed, inject, Injectable, signal } from '@angular/core';
import { AttrPath } from '@cumlaude/metadata';
import { InstellingBron, RCumLaudeModule, RRol } from '@cumlaude/service-contract';
import { AttOverrides, CustomFilter } from '@cumlaude/shared-dashboard-registry';
import { Data } from '@angular/router';
import { UserService } from './user.service';
import { intersection, isUndefined } from 'lodash-es';

export type UserRouteData = CumLaudeRouteData & {
	/**
	 * Bepaalt of het menu item in het top-level-menu moet worden weergegeven. Alleen toevoegen indien {{base}} niet is ingesteld.
	 */
	top?: boolean;
	/**
	 * Bepaalt of het menu item ook een root groep is. Gebeurt al automatisch indien deze op het onderste niveau in de route structuur zit.
	 */
	root?: boolean;
	/**
	 * Naam van het menu item indien deze afwijkt van de naam van het dashboard.
	 */
	tab?: string;
	/**
	 * De vaste weergave-opties van het dashboard, die behouden blijven als je wisselt van dashboard.
	 * Deze opties worden niet gereset door de resetten knop.
	 */
	fixedWeergaveOpties?: string[];
	/**
	 * De flexibele weergave-opties van het dashboard, die behouden blijven als je wisselt van dashboard.
	 * Deze opties worden wel gereset door de resetten knop.
	 */
	flexibleWeergaveOpties?: string[];
	/**
	 * De standaard groeperingen van dit dashboard.
	 */
	defaultGroeperingen?: AttrPath[];
	/**
	 * Zie RegistryNode.attOverrides.
	 */
	attOverrides?: AttOverrides;
	/**
	 * Bepaalt of de groepering query parameter behouden moet worden bij het wisselen van dashboard. Wordt alleen toegepast in het tab-menu.
	 */
	keepGroepering?: boolean;
	/**
	 * De rollen die toegang hebben tot deze route.
	 */
	requiredRollen?: RRol[];
	/**
	 * Vereiste module om deze route te kunnen gebruiken.
	 */
	requiredModule?: RCumLaudeModule;
	/**
	 * Vereiste bron om deze route te kunnen gebruiken.
	 */
	requiredBron?: InstellingBron;
	/**
	 * Of je supportrecht moet hebben om deze route te kunnen gebruiken.
	 */
	requiresSupport?: boolean;
	/**
	 * Indien true, dan alleen toegankelijk als de gebruiker een VO-vestiging heeft.
	 */
	vo?: boolean;
	/**
	 * Indien true, dan alleen toegankelijk als de gebruiker een VSO-vestiging heeft.
	 */
	vso?: boolean;
	/**
	 * Het maximale aantal groeperingen dat dit dashboard aankan. Zie RegistryNode.maxGroeperingen.
	 */
	maxGroeperingen?: number;
	/**
	 * De enige groeperingen die dit dashboard aankan, in punt-notatie. Zie
	 * RegistryNode.toegestaneGroeperingen.
	 */
	toegestaneGroeperingen?: string[];
	/**
	 * De velden waarop dit dashboard zelf al uitsplitst, in punt-notatie. Zie RegistryNode.subgroepen.
	 */
	subgroepen?: string[];
	/**
	 * De custom filters van dit dashboard, voor de AI-chat. Zie RegistryNode.customFilters.
	 */
	customFilters?: readonly CustomFilter[];
	/**
	 * Icoon voor de verticale hoofdnavigatie. Alleen ingesteld op top-levelroutes (hoofdpagina's).
	 */
	icon?: IconName;
	/**
	 * Geeft aan dat deze node zijn eigen (bestaande) tabbalk met subpagina's toont in plaats van dat
	 * de subpagina's in de verticale hoofdnavigatie worden uitgeklapt.
	 */
	eigenTabbalk?: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class UserRoutesService extends AppRoutesService<UserRouteData> {
	readonly userService = inject(UserService);

	override routeConfig = signal(this.router.config);

	private readonly authorisedRoutes = computed(() => {
		const routes = this.routes();
		const vo = this.userService.vo();
		const vso = this.userService.vso();
		const modules = this.userService.modules();
		const bron = this.userService.bron();

		return this.findAuthorisedRoutes(routes, modules, vo, vso, bron);
	});

	override activeAuthorisedRoutes = computed(() => {
		const activeRol = this.userService.activeRol();
		const beheerder = intersection(this.userService.rollen(), [RRol.BEHEERDER]);
		const activeRollen = [...(activeRol ? [activeRol] : []), ...beheerder];
		const support = this.userService.account()?.support ?? false;

		if (activeRollen.length === 0) return [];

		const authorisedRoutes = this.authorisedRoutes();
		if (authorisedRoutes.length === 0) return [];

		return this.findRolAuthorisedRoutes(authorisedRoutes, activeRollen, support);
	});

	override isRoot(route: CumLaudeRoute<UserRouteData>): boolean {
		return route.data?.root || false;
	}

	override hasName(data: UserRouteData): boolean {
		return !isUndefined(data.name);
	}

	findAuthorisedRoutesForRollen(rollen: RRol[]) {
		const authorisedRoutes = this.authorisedRoutes();
		return this.findRolAuthorisedRoutes(authorisedRoutes, rollen, false);
	}

	private findAuthorisedRoutes(
		originalRoutes: RouteDef<UserRouteData>[],
		modules: RCumLaudeModule[],
		vo: boolean,
		vso: boolean,
		bron: InstellingBron | undefined
	) {
		const routes: RouteDef<UserRouteData>[] = [];
		for (const route of originalRoutes) {
			const routeData = route.routeData!;
			if (routeData.requiredModule && !modules.includes(routeData.requiredModule)) continue;
			if (routeData.vo && !vo) continue;
			if (routeData.vso && !vso) continue;
			if (routeData.requiredBron && bron !== routeData.requiredBron) continue;

			const originalSubRoutes = route.subRoutes;
			if (!originalSubRoutes) {
				routes.push(route);
				continue;
			}

			const authorisedSubRoutes = this.findAuthorisedRoutes(originalSubRoutes, modules, vo, vso, bron);
			if (authorisedSubRoutes.length > 0) routes.push({ ...route, subRoutes: authorisedSubRoutes });
		}

		return routes;
	}

	private findRolAuthorisedRoutes(authorisedRoutes: RouteDef<UserRouteData>[], rollen: RRol[], support: boolean) {
		const routes: RouteDef<UserRouteData>[] = [];
		for (const route of authorisedRoutes) {
			const routeData = route.routeData!;
			const requiredRollen = routeData.requiredRollen;
			if (requiredRollen && !requiredRollen.some((rol) => rollen.includes(rol))) continue;
			if (routeData.requiresSupport && !support) continue;

			const authorisedSubRoutes = route.subRoutes;
			if (authorisedSubRoutes) {
				const activeSubRoutes = this.findRolAuthorisedRoutes(authorisedSubRoutes, rollen, support);
				if (activeSubRoutes.length > 0) routes.push({ ...route, subRoutes: activeSubRoutes });
			} else routes.push(route);
		}

		return routes;
	}
}

export function isTopLevelRoute(routeData: UserRouteData): boolean {
	return routeData.top ?? routeData.base !== undefined;
}

export function routeNaam(routeData: UserRouteData): string {
	return routeData.tab ?? routeData.name!;
}

export function getWeergaveOpties(route: RouteDef<UserRouteData>): string[] {
	const routeData = route.routeData!;
	return [...(routeData.fixedWeergaveOpties ?? []), ...(routeData.flexibleWeergaveOpties ?? [])];
}

export function getWeergaveOptiesWithGroepering(route: RouteDef<UserRouteData>) {
	const routeData = route.routeData!;
	return [...getWeergaveOpties(route), ...(routeData.keepGroepering ? ['g'] : [])];
}

const GEEN_ATT_OVERRIDES: AttOverrides = {};

/**
 * De attribuut-overrides van het dashboard waar deze route bij hoort. Geeft steeds hetzelfde lege
 * object terug als er geen zijn, zodat de uitkomst als @Input doorgegeven kan worden zonder bij elke
 * change detection een nieuwe referentie op te leveren.
 */
export function attOverridesUitRouteData(data: Data | undefined): AttOverrides {
	return (<UserRouteData | undefined>data)?.attOverrides ?? GEEN_ATT_OVERRIDES;
}
